/**
 * Typed client for the share endpoints.
 *
 * Two audiences share one transport:
 * - PUBLIC recipient reads (`verify` / `getProfile` / `getRegionalTrend`) — no auth.
 * - AUTHED owner-mode calls (`previewMine` / `getMine` / `regenerate` / `update` /
 *   `revoke` / `previewMineRegionalTrend`) — the owner's Firebase ID token rides an
 *   `Authorization: Bearer` header. Owner mode runs inside a WebView the app hosts,
 *   which injects the token (see ownerToken.ts).
 *
 * The web app holds zero business logic — it exchanges credentials for data and
 * renders the payload the backend assembles. Transport is tRPC over HTTP with a
 * superjson transformer, so inputs are superjson-serialized and results
 * superjson-deserialized here. Every response is re-validated against the shared
 * Zod schema at this boundary, so a shape drift surfaces here rather than deep in
 * a component. Mutations POST (the credential rides the body/header, never the
 * URL); the one query (`getMine`) is a GET with the token in the header.
 */
import {
  APISharedProfileResponseSchema,
  APIShareMineSchema,
  APIShareRegenerateResponseSchema,
  APIShareRegionalTrendResponseSchema,
  APIShareVerifyResponseSchema,
  shareVerifyErrorReason,
  type APISharedProfileResponse,
  type APIShareMine,
  type APIShareRegenerateRequest,
  type APIShareRegenerateResponse,
  type APIShareRegionalTrendResponse,
  type APIShareUpdateRequest,
  type APIShareVerifyResponse,
  type ShareVerifyErrorReason,
  type TechName,
} from '@see-stella/types'
import superjson, { type SuperJSONResult } from 'superjson'
import { z } from 'zod'
import { API_BASE_URL } from './env'
import { logError } from './logger'

// ─── Static-template fixture mode ─────────────────────────────────────────────
// When built with VITE_SHARE_FIXTURE=1, the app is a self-contained template: no
// backend, no live token. The three PUBLIC procedures are served from a baked
// snapshot (one consented profile captured from the real backend, photos bundled
// under /joanne/). The snapshot stores each proc's raw superjson `result.data`
// envelope, so it flows through the SAME unwrap + deserialize + Zod validation as
// a live response — Dates and all. Owner-mode calls are never issued in this mode.
const FIXTURE_MODE = import.meta.env.VITE_SHARE_FIXTURE === '1'

interface FixtureEnvelopes {
  verify: unknown
  profile: unknown
  regional: Record<string, unknown>
}

let fixturePromise: Promise<FixtureEnvelopes> | null = null
function loadFixture(): Promise<FixtureEnvelopes> {
  if (!fixturePromise) {
    fixturePromise = fetch(`${import.meta.env.BASE_URL}joanne/data.json`).then(
      (r) => {
        if (!r.ok) throw new ShareApiError('network', `fixture ${r.status}`)
        return r.json() as Promise<FixtureEnvelopes>
      }
    )
  }
  return fixturePromise
}

/** Serve one public proc from the baked snapshot, mirroring shareMutation's unwrap. */
async function fixtureMutation(proc: string, input: unknown): Promise<unknown> {
  const fx = await loadFixture()
  const envelope =
    proc === 'verify'
      ? fx.verify
      : proc === 'getProfile'
        ? fx.profile
        : proc === 'getRegionalTrend'
          ? fx.regional[(input as { techName?: string })?.techName ?? '']
          : undefined
  if (envelope === undefined) throw new ShareApiError('not_found')
  return superjson.deserialize<{ data: unknown }>(envelope as SuperJSONResult)
    .data
}

/** A reason the UI knows how to render, plus the raw transport code. */
export class ShareApiError extends Error {
  readonly reason: ShareVerifyErrorReason | 'network' | 'unknown'
  constructor(reason: ShareApiError['reason'], message?: string) {
    super(message ?? reason)
    this.name = 'ShareApiError'
    this.reason = reason
  }
}

// Validate the reason against the shared enum (single source of truth) so a new
// backend reason can't silently degrade to 'unknown' without a compile touch here.
const reasonSchema = z.enum(shareVerifyErrorReason)

function toReason(message: unknown): ShareApiError['reason'] {
  const parsed = reasonSchema.safeParse(message)
  return parsed.success ? parsed.data : 'unknown'
}

/** tRPC error envelope → ShareApiError. */
function throwTrpcError(body: unknown): never {
  const err = (body as { error?: { json?: { message?: unknown } } })?.error
  throw new ShareApiError(toReason(err?.json?.message))
}

/** Unwrap a tRPC success envelope, deserializing the superjson data payload. */
function unwrapResult<T>(body: unknown): T {
  const result = (body as { result?: { data?: unknown } })?.result
  if (!result || result.data === undefined) throwTrpcError(body)
  return superjson.deserialize<T>(result.data as SuperJSONResult)
}

async function readBody(res: Response): Promise<unknown> {
  try {
    return await res.json()
  } catch {
    throw new ShareApiError('network', `Malformed response (${res.status})`)
  }
}

/** Re-validate a payload against its schema; a drift surfaces here as 'unknown'. */
function validate<T>(schema: z.ZodType<T>, data: unknown, label: string): T {
  const parsed = schema.safeParse(data)
  if (!parsed.success)
    throw new ShareApiError('unknown', `Invalid ${label} shape`)
  return parsed.data
}

/**
 * POST a share mutation and return the inner `data` payload (the IBaseAPIResponse
 * `.data`). An owner call passes `idToken`, adding the Bearer header; a public call
 * omits it. Never let a shared cache retain the credential or the response.
 */
async function shareMutation(
  proc: string,
  input: unknown,
  idToken?: string
): Promise<unknown> {
  // Template mode: the public read path is served from the baked snapshot.
  if (FIXTURE_MODE && !idToken) return fixtureMutation(proc, input)
  let res: Response
  try {
    res = await fetch(`${API_BASE_URL}/v1.share.${proc}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(idToken ? { Authorization: `Bearer ${idToken}` } : {}),
      },
      body: JSON.stringify(superjson.serialize(input)),
      cache: 'no-store',
    })
  } catch (e) {
    logError(`share.${proc} network error`, e)
    throw new ShareApiError('network')
  }
  const body = await readBody(res)
  return unwrapResult<{ data: unknown }>(body).data
}

/** GET a share query (owner-only) with the Bearer header. `getMine` is the one query. */
async function shareQuery(proc: string, idToken: string): Promise<unknown> {
  let res: Response
  try {
    res = await fetch(`${API_BASE_URL}/v1.share.${proc}`, {
      method: 'GET',
      headers: { Authorization: `Bearer ${idToken}` },
      cache: 'no-store',
    })
  } catch (e) {
    logError(`share.${proc} network error`, e)
    throw new ShareApiError('network')
  }
  const body = await readBody(res)
  return unwrapResult<{ data: unknown }>(body).data
}

// ─── PUBLIC recipient reads ───────────────────────────────────────────────────

/**
 * Exchange the URL token (plus an optional 6-digit code) for a short-lived view
 * token. Link-only shares open code-free — pass no code and the field is omitted
 * from the request entirely (the backend skips the code check when the link
 * carries no access code). Legacy coded links still require the code: opening one
 * code-free returns invalid_code, which the UI uses to fall back to the gate.
 * Wrong code → invalid_code; expired/revoked → expired/not_found; throttled → locked.
 */
export async function verify(
  token: string,
  accessCode?: string
): Promise<APIShareVerifyResponse> {
  // Only send accessCode when supplied, so a code-free open posts { token } alone.
  const input = accessCode === undefined ? { token } : { token, accessCode }
  const data = await shareMutation('verify', input)
  return validate(APIShareVerifyResponseSchema, data, 'verify')
}

/** Resolve a view token into the shared profile payload. */
export async function getProfile(
  viewToken: string,
  locale?: string
): Promise<APISharedProfileResponse> {
  const data = await shareMutation('getProfile', { viewToken, locale })
  return validate(APISharedProfileResponseSchema, data, 'profile')
}

/**
 * Lazily fetch the per-region trend for one metric across all scans. Kept separate
 * from getProfile so the base payload stays lean — called only when the viewer
 * opens region detail.
 */
export async function getRegionalTrend(
  viewToken: string,
  techName: TechName,
  locale?: string
): Promise<APIShareRegionalTrendResponse> {
  const data = await shareMutation('getRegionalTrend', {
    viewToken,
    techName,
    locale,
  })
  return validate(APIShareRegionalTrendResponseSchema, data, 'regional trend')
}

// ─── AUTHED owner-mode calls ──────────────────────────────────────────────────
// The owner's Firebase ID token (injected into the WebView) authorizes these. The
// owner preview reuses the recipient render path with the FULL profile.

/** The owner's own full profile preview (all sections; the control bar hides them). */
export async function authedPreviewMine(
  idToken: string,
  locale?: string
): Promise<APISharedProfileResponse> {
  const data = await shareMutation('previewMine', { locale }, idToken)
  return validate(APISharedProfileResponseSchema, data, 'owner profile')
}

/** The owner preview's per-region trend for one metric. */
export async function authedPreviewRegionalTrend(
  idToken: string,
  techName: TechName,
  locale?: string
): Promise<APIShareRegionalTrendResponse> {
  const data = await shareMutation(
    'previewMineRegionalTrend',
    { techName, locale },
    idToken
  )
  return validate(
    APIShareRegionalTrendResponseSchema,
    data,
    'owner regional trend'
  )
}

/** The owner's active share link (null when they have none). */
export async function authedGetMine(
  idToken: string
): Promise<APIShareMine | null> {
  const data = await shareQuery('getMine', idToken)
  return validate(APIShareMineSchema.nullable(), data, 'owner share')
}

/** Mint (or replace) the owner's share link with the chosen sections + expiry. */
export async function authedRegenerate(
  idToken: string,
  req: APIShareRegenerateRequest
): Promise<APIShareRegenerateResponse> {
  const data = await shareMutation('regenerate', req, idToken)
  return validate(APIShareRegenerateResponseSchema, data, 'regenerate')
}

/** Update the active link's shared sections / expiry in place. */
export async function authedUpdate(
  idToken: string,
  req: APIShareUpdateRequest
): Promise<APIShareMine> {
  const data = await shareMutation('update', req, idToken)
  return validate(APIShareMineSchema, data, 'owner share')
}

/** Stop sharing — revoke the owner's active link. */
export async function authedRevoke(idToken: string): Promise<void> {
  await shareMutation('revoke', {}, idToken)
}
